<script setup>
import { ref, defineProps, defineEmits } from "vue";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import { useTerritoriosStore } from "@/store/territorios";
import { useCatalogos } from "@/composables/useCatalogos";
import { useAppToast } from "@/composables/useAppToast";

defineProps({
  visible: Boolean,
});
const emit = defineEmits(["update:visible"]);

const territorios = useTerritoriosStore();
const cat = useCatalogos();
const toast = useAppToast();

const saving = ref(false);
const saved = ref(false);

const addZona = async () => {
  const created = await cat.createZona({ name: "" });
  if (created?.error) return toast.error("Ocurrió un error", created.error);
  territorios.zonas.push({ id: created.id, name: created.name ?? "" });
};

const removeZona = async (id) => {
  const res = await cat.deleteZona(id);
  if (res?.error) return toast.error("Ocurrió un error", res.error);
  territorios.zonas = territorios.zonas.filter((z) => z.id !== id);
  // El servidor ya desasoció la zona de los grupos; reflejarlo en el store local
  territorios.grupos.forEach((g) => {
    if (g.zonaId === id) g.zonaId = null;
  });
};

const save = async () => {
  saving.value = true;
  try {
    await Promise.all(territorios.zonas.map((z) => cat.updateZona(z.id, { name: z.name })));
  } catch (e) {
    console.error(e);
    toast.error("Ocurrió un error", "No se pudieron guardar los cambios.");
    saving.value = false;
    return;
  }
  saving.value = false;
  saved.value = true;
  setTimeout(() => (saved.value = false), 2500);
};
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="(v) => emit('update:visible', v)"
    modal
    header="Zonas"
    :style="{ width: '460px' }"
  >
    <div class="text-sm text-500 mb-3">Otras congregaciones. Un grupo puede pertenecer a una zona.</div>
    <div class="item-list">
      <div v-for="item in territorios.zonas" :key="item.id" class="item-row">
        <InputText v-model="item.name" class="flex-1" placeholder="Nombre de la zona" />
        <Button
          icon="pi pi-trash"
          text
          rounded
          severity="danger"
          size="small"
          v-tooltip.top="'Eliminar'"
          @click="removeZona(item.id)"
        />
      </div>
      <div v-if="!territorios.zonas.length" class="empty-state">
        Sin zonas configuradas
      </div>
    </div>
    <Button
      icon="pi pi-plus"
      label="Agregar zona"
      outlined
      size="small"
      class="mt-2"
      @click="addZona"
    />
    <template #footer>
      <Button label="Cerrar" text @click="emit('update:visible', false)" />
      <Button
        :label="saved ? 'Guardado' : 'Guardar'"
        :icon="saved ? 'pi pi-check' : 'pi pi-save'"
        :loading="saving"
        @click="save"
      />
    </template>
  </Dialog>
</template>

<style scoped>
.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.item-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.empty-state {
  font-size: var(--text-callout);
  color: var(--label-tertiary);
  padding: 0.5rem 0;
  font-style: italic;
}
</style>
